import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { DEFAULT_IMAGES, DEFAULT_CONTACT, DEFAULT_FEATURED, DEFAULT_MENU, normalizeFeatured } from "@/lib/defaults";
import { setWhatsAppNumber } from "@/lib/site";
import { normalizeLegacyBarcaDoReiMenu, removeImperioFromMenu } from "@/lib/menuData";

const LanguageContext = createContext();

const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);
function deepMerge(base, over) {
  if (!isObj(base) || !isObj(over)) return over === undefined ? base : over;
  const out = { ...base };
  for (const k of Object.keys(over)) {
    out[k] = isObj(base[k]) && isObj(over[k]) ? deepMerge(base[k], over[k]) : over[k];
  }
  return out;
}

export const LANGS = [
  { id: "pt", label: "PT" },
  { id: "en", label: "EN" },
  { id: "es", label: "ES" },
];

export const LOCALES = { pt: "pt-BR", en: "en-US", es: "es-ES" };

export const TRANSLATIONS = {
  pt: {
    nav: {
      cardapio: "Cardápio",
      historia: "Nossa História",
      galeria: "Galeria",
      comoChegar: "Como Chegar",
      reservar: "Reservar",
      reservarMobile: "Reservar Mesa",
      whatsappAria: "Falar no WhatsApp",
      openMenu: "Abrir menu",
      closeMenu: "Fechar menu",
    },
    hero: {
      badge: "Praia da Sete Fontes · Ubatuba — SP",
      line1: "Entre a mata e o mar,",
      line2: "uma história de sabor.",
      line3: "",
      subtitle:
        "Desde 1996, quando ainda éramos o Bambuzinho, celebramos a tradição caiçara na Praia das Sete Fontes, onde a pesca do dia vira moqueca e a caipirinha chega antes do pôr do sol.",
      ctaMenu: "Explorar Cardápio",
      ctaReserve: "Garantir Mesa",
      stats: ["Peixe fresco do dia", "Caipirinhas artesanais", "Acesso por trilha ou barco"],
      scrollAria: "Rolar para o cardápio",
    },
    manifesto: {
      eyebrow: "Nossa História",
      titleA: "Três capítulos de uma vida",
      titleB: "à beira-mar",
      chapters: [
        {
          title: "O refúgio secreto da Sete Fontes",
          text: "Não se chega aqui de carro — e é justamente esse o encanto. Uma trilha leve pela Mata Atlântica.",
        },
        {
          title: "Da canoa para a panela de barro",
          text: "Nossa pesca tem nome e tradição: Sr. Domingos. Pescador desde menino, ele mantém viva a tradição da canoa caiçara, trazendo do mar o frescor que chega à nossa mesa. Da canoa à cozinha, preparados com todo o capricho caiçara.",
        },
        {
          title: "Hospitalidade de pé descalço",
          text: "Aqui o dress code é chinelo e sal na pele. Espreguiçadeira para descansar, sombra de árvore centenária, cerveja gelada e um fim de tarde que parece não acabar nunca.",
        },
      ],
    },
    menu: {
      eyebrow: "Cardápio",
      titleA: "Sabores que chegam",
      titleB: "com a maré",
      subtitle:
        "Cardápio oficial da casa, direto do balcão para o site. Em caso de dúvida sobre disponibilidade do dia, confirme pelo WhatsApp.",
      categories: {
        petiscos: "Porções & à Dorê",
        moquecas: "Moquecas",
        pratos: "Grelhados & Combinados",
        drinks: "Caipirinhas & Drinks",
        bebidas: "Cervejas, Sucos & Bebidas",
        extras: "Saladas & Extras",
      },
      groups: {
        porcoes: "Porções",
        adore: "à Dorê",
        moquecas: "Moquecas",
        grelhados: "Grelhados",
        combinados: "Combinados",
        vinagrete: "Vinagrete Provençal",
        caipirinhas: "Caipirinhas",
        batidas: "Batidas",
        doses: "Drinks & Doses",
        cervejas: "Cervejas",
        sucos: "Sucos Naturais",
        bebidasGelo: "Bebidas & Gelo",
        saladas: "Saladas",
        extras: "Extras",
        sobremesas: "Sobremesas",
      },
      notes: {
        moquecas: "Serve 3 pessoas · Acompanha arroz e pirão",
        grelhados: "Serve 3 pessoas · Acompanha arroz e farofa de banana",
        combinados: "Serve 3 pessoas",
        vinagrete: "Acompanha pão",
      },
      tags: { house: "Especialidade da Casa", share: "Para Compartilhar" },
      featured: {
        badge: "Especialidade da Casa",
        desc: "Filé de peixe grelhado (sororoca) com molho especial de lula. Acompanha arroz e salada mista com palmito e manga ou batatas fritas.",
        order: "Pedir no WhatsApp",
      },
    },
    gallery: {
      eyebrow: "Galeria",
      titleA: "Um pedaço do paraíso,",
      titleB: "quadro a quadro",
      filters: { todos: "Todos", quiosque: "O Quiosque", praia: "A Praia", pratos: "Os Pratos" },
      captions: {
        tropical: "A trilha pela Mata Atlântica",
        shrimp: "Banquete caiçara à beira-mar",
        coconut: "Caipirinha de limão da casa",
        sunset: "O fim de tarde da Sete Fontes",
        caipirinha: "Drinks tropicais à beira-mar",
        surf: "Mar aberto para todos os níveis",
        seafood: "Barca de frutos do mar",
        beach: "Água cristalina o ano inteiro",
        baiaAerea: "Nossa enseada vista de cima",
        aguaCristalina: "Água transparente até o fundo",
        lanchas: "Lanchas fundeadas na baía",
        praiaMata: "Areia dourada abraçada pela mata",
      },
      closeAria: "Fechar foto",
    },
    reservation: {
      eyebrow: "Reservas",
      titleA: "Garanta sua mesa",
      titleB: "com os pés na areia",
      subtitle:
        "Nos fins de semana e feriados a Sete Fontes enche cedo. Deixe sua reserva e a gente guarda o melhor lugar — da sombra da árvore ao primeiro banco da areia.",
      bullets: [
        "Confirmação rápida pelo WhatsApp",
        "Grupos e aniversários são bem-vindos",
      ],
      form: {
        name: "Nome completo *",
        namePh: "Como devemos te chamar?",
        phone: "WhatsApp / Telefone *",
        phonePh: "(12) 99999-9999",
        date: "Data desejada *",
        guests: "Quantidade de pessoas",
        guestOne: "pessoa",
        guestMany: "pessoas",
        guestGroup: "Grupo (9+ pessoas)",
        seating: "Preferência de mesa",
        seatingOptions: [
          { id: "Pé na areia", label: "Pé na areia" },
          { id: "Deck de madeira coberto", label: "Deck de madeira coberto" },
          { id: "Sombra das árvores", label: "Sombra das árvores" },
          { id: "Sem preferência", label: "Sem preferência" },
        ],
        message: "Observações",
        messagePh: "Aniversário, cadeira infantil, restrição alimentar...",
        submit: "Reservar minha mesa",
        sending: "Enviando...",
        note: "Ao enviar, você poderá confirmar direto no nosso WhatsApp.",
        errorInvalid: "Preencha nome, telefone e data para reservar.",
        errorFail: "Não conseguimos registrar agora. Tente pelo botão do WhatsApp.",
        successToast: "Reserva recebida! Confirme pelo WhatsApp para garantir sua mesa.",
      },
      success: {
        title: "Reserva anotada, {name}!",
        textPre: "Recebemos seu pedido para",
        people: "pessoa(s)",
        textMid: "no dia",
        table: "mesa",
        textPost: "Para garantir, confirme com a gente no WhatsApp:",
        whatsapp: "Confirmar no WhatsApp",
        again: "Fazer outra reserva",
      },
    },
    reach: {
      eyebrow: "Como Chegar",
      titleA: "O caminho faz parte",
      titleB: "da experiência",
      subtitle:
        "A Sete Fontes não tem acesso de carro — e é por isso que continua selvagem. Escolha sua aventura:",
      options: [
        {
          title: "Trilha a pé pela Mata Atlântica",
          time: "40–50 min",
          level: "Leve a moderada",
          steps: [
            "Comece no Saco da Ribeira, em Ubatuba.",
            "Siga a trilha demarcada pela mata, passando pela praia da Ribeira e praia do Flamengo.",
            "Ao avistar o mar aberto, desça pela areia — o quiosque é o primeiro à direita.",
          ],
        },
        {
          title: "Barco, escuna ou taxi boat",
          time: "10–15 min",
          level: "Sem esforço",
          steps: [
            "Embarque no Saco da Ribeira, Perequê-Mirim ou onde for combinado.",
            "Combine ida e volta com o barqueiro — eles conhecem o Quiosque Rainha do Mar.",
            "A travessia passa pelas praias, ilha e desembarca direto na nossa areia.",
          ],
        },
      ],
      tipsTitle: "Dicas de quem é da casa",
      tips: [
        "Leve água e dinheiro em espécie — o sinal de celular é fraco na praia",
        "Protetor solar e chapéu: a sombra é disputada ao meio-dia",
        "Na trilha, use calçado firme — tem trecho de pedra e raiz",
      ],
      coordsLabel: "Coordenadas",
      directions: "Traçar rota no Google Maps",
      mapTitle: "Mapa da Praia da Sete Fontes, Ubatuba",
    },
    trail: {
      title: "O mapa da trilha",
      subtitle: "Do Saco da Ribeira ao nosso balcão, passando pelas sete fontes de água doce.",
      start: "Saco da Ribeira",
      startSub: "início da trilha",
      springs: "As 7 Fontes",
      springsSub: "água doce na areia",
      end: "Rainha do Mar",
      endSub: "seu quiosque",
      leg1: "~25 min",
      leg2: "~15 min",
      legendWalk: "caminhada leve pela mata",
      legendTotal: "total: 30–40 min",
    },
    weather: {
      title: "Agora na Sete Fontes",
      loading: "Consultando o céu...",
      unavailable: "Previsão indisponível no momento",
      wind: "vento",
      waves: "ondas",
      sea: "mar",
      conditions: {
        clear: "Céu limpo",
        partly: "Parcialmente nublado",
        cloudy: "Nublado",
        fog: "Nevoeiro",
        drizzle: "Garoa",
        rain: "Chuva",
        storm: "Tempestade",
      },
      seaState: { calm: "mar calmo", moderate: "mar moderado", rough: "mar agitado" },
    },
    footer: {
      brandSub: "Quiosque Caiçara",
      blurb:
        "Cozinha caiçara de verdade, drinks artesanais e a areia mais exclusiva de Ubatuba. Chegue de trilha ou de barco — o resto é com a gente.",
      hoursTitle: "Horário de Funcionamento",
      hours: [
        "Sábado e domingo · 09h às 17h",
        "Alta temporada e feriados · todos os dias",
        "Se as condições de mar e tempo forem favoráveis",
      ],
      reserveCta: "Reservar mesa →",
      whereTitle: "Onde Estamos",
      addressLine1: "Praia da Sete Fontes, s/n°",
      addressLine2: "Ubatuba — Litoral Norte de São Paulo",
      addressLine3: "Acesso por trilha (Praia Grande) ou barco (Saco da Ribeira)",
      whatsappCta: "Chamar no WhatsApp",
      madeWith: "Feito com sal, sol e dendê.",
    },
    ambience: {
      soundOn: "Desligar o som do mar",
      soundOff: "Ouvir o som do mar",
      phaseLabel: { day: "Dia de praia", golden: "Golden hour", night: "Noite caiçara" },
    },
    whatsappAria: "Conversar no WhatsApp",
  },

  en: {
    nav: {
      cardapio: "Menu",
      historia: "Our Story",
      galeria: "Gallery",
      comoChegar: "Getting Here",
      reservar: "Book",
      reservarMobile: "Book a Table",
      whatsappAria: "Chat on WhatsApp",
      openMenu: "Open menu",
      closeMenu: "Close menu",
    },
    hero: {
      badge: "Sete Fontes Beach · Ubatuba — SP, Brazil",
      line1: "Toes in the sand,",
      line2: "breeze on your face",
      line3: "and the taste of the caiçara sea.",
      subtitle:
        "A thatched-roof kiosk hidden between the Atlantic Forest and the sea, where the day's catch becomes moqueca and the caipirinha arrives before sunset.",
      ctaMenu: "Explore the Menu",
      ctaReserve: "Book a Table",
      stats: ["Fresh catch of the day", "Craft caipirinhas", "Trail or boat access"],
      scrollAria: "Scroll to the menu",
    },
    manifesto: {
      eyebrow: "Our Story",
      titleA: "Three chapters of a life",
      titleB: "by the sea",
      chapters: [
        {
          title: "The secret refuge of Sete Fontes",
          text: "You can't get here by car — and that's exactly the charm. A light trail through the Atlantic Forest, seven freshwater springs bubbling from the sand and, at the end of the path, our thatched kiosk waiting with the table set.",
        },
        {
          title: "From the boat to the clay pot",
          text: "We work with local fishermen: what the sea gives in the morning, the kitchen gives back at lunch. Fresh grouper, pink shrimp and squid, prepared with caiçara care — dendê, cilantro and wild lime.",
        },
        {
          title: "Barefoot hospitality",
          text: "Here the dress code is flip-flops and salt on your skin. A hammock to rest, the shade of a century-old tree, beer chilling in the cooler and a late afternoon that never seems to end.",
        },
      ],
    },
    menu: {
      eyebrow: "Menu",
      titleA: "Flavors that arrive",
      titleB: "with the tide",
      subtitle:
        "The house's official menu, straight from the counter to the site. To check the day's availability, confirm on WhatsApp.",
      categories: {
        petiscos: "Snacks & à Dorê",
        moquecas: "Moquecas",
        pratos: "Grilled & Combos",
        drinks: "Caipirinhas & Drinks",
        bebidas: "Beers, Juices & Drinks",
        extras: "Salads & Sides",
      },
      groups: {
        porcoes: "Snacks",
        adore: "à Dorê",
        moquecas: "Moquecas",
        grelhados: "Grilled",
        combinados: "Combos",
        vinagrete: "Provençal Vinagrete",
        caipirinhas: "Caipirinhas",
        batidas: "Batidas",
        doses: "Drinks & Shots",
        cervejas: "Beers",
        sucos: "Fresh Juices",
        bebidasGelo: "Drinks & Ice",
        saladas: "Salads",
        extras: "Sides",
        sobremesas: "Desserts",
      },
      notes: {
        moquecas: "Serves 3 · Comes with rice and pirão",
        grelhados: "Serves 3 · Comes with rice and banana farofa",
        combinados: "Serves 3",
        vinagrete: "Comes with bread",
      },
      tags: { house: "House Specialty", share: "For Sharing" },
      featured: {
        badge: "House Specialty",
        desc: "Grilled sororoca fish fillet with special squid sauce. Served with rice and mixed salad with hearts of palm and mango, or fries.",
        order: "Order on WhatsApp",
      },
    },
    gallery: {
      eyebrow: "Gallery",
      titleA: "A slice of paradise,",
      titleB: "frame by frame",
      filters: { todos: "All", quiosque: "The Kiosk", praia: "The Beach", pratos: "The Dishes" },
      captions: {
        tropical: "The trail through the Atlantic Forest",
        shrimp: "Caiçara feast by the sea",
        coconut: "House lime caipirinha",
        sunset: "Golden hour at Sete Fontes",
        caipirinha: "Tropical drinks by the sea",
        surf: "Open sea for all levels",
        seafood: "Seafood boat platter",
        beach: "Crystal-clear water all year round",
        baiaAerea: "Our cove seen from above",
        aguaCristalina: "Water so clear you see the bottom",
        lanchas: "Boats anchored in the bay",
        praiaMata: "Golden sand embraced by the forest",
      },
      closeAria: "Close photo",
    },
    reservation: {
      eyebrow: "Reservations",
      titleA: "Secure your table",
      titleB: "with your toes in the sand",
      subtitle:
        "On weekends and holidays Sete Fontes fills up early. Leave your reservation and we'll keep the best spot — from the tree shade to the first seat on the sand.",
      bullets: [
        "Quick confirmation via WhatsApp",
        "Groups and birthdays are welcome",
        "Tuesday to Sunday, 9am to 6pm",
      ],
      form: {
        name: "Full name *",
        namePh: "What should we call you?",
        phone: "WhatsApp / Phone *",
        phonePh: "+55 12 99999-9999",
        date: "Preferred date *",
        guests: "Number of guests",
        guestOne: "person",
        guestMany: "people",
        guestGroup: "Group (9+ people)",
        seating: "Table preference",
        seatingOptions: [
          { id: "Pé na areia", label: "Feet in the sand" },
          { id: "Deck de madeira coberto", label: "Covered wooden deck" },
          { id: "Sombra das árvores", label: "Tree shade" },
          { id: "Sem preferência", label: "No preference" },
        ],
        message: "Notes",
        messagePh: "Birthday, high chair, dietary restriction...",
        submit: "Book my table",
        sending: "Sending...",
        note: "After sending, you can confirm directly on our WhatsApp.",
        errorInvalid: "Please fill in name, phone and date to book.",
        errorFail: "We couldn't register right now. Please try the WhatsApp button.",
        successToast: "Reservation received! Confirm on WhatsApp to secure your table.",
      },
      success: {
        title: "Reservation noted, {name}!",
        textPre: "We received your request for",
        people: "guest(s)",
        textMid: "on",
        table: "table",
        textPost: "To secure it, confirm with us on WhatsApp:",
        whatsapp: "Confirm on WhatsApp",
        again: "Make another reservation",
      },
    },
    reach: {
      eyebrow: "Getting Here",
      titleA: "The journey is part",
      titleB: "of the experience",
      subtitle:
        "Sete Fontes has no car access — and that's why it stays wild. Choose your adventure:",
      options: [
        {
          title: "Hiking trail through the Atlantic Forest",
          time: "30–40 min",
          level: "Easy to moderate",
          steps: [
            "Start at Saco da Ribeira, in Ubatuba.",
            "Follow the marked trail through the forest, past the freshwater springs that give the beach its name.",
            "When the open sea appears, head down the sand — the thatched kiosk is the first on the left.",
          ],
        },
        {
          title: "Boat or schooner taxi",
          time: "10–15 min",
          level: "Effortless",
          steps: [
            "Board at Saco da Ribeira or at the Praia Grande pier.",
            "Arrange the round trip with the boatman — they all know Quiosque Rainha do Mar.",
            "The crossing passes the islands and drops you right on our sand.",
          ],
        },
      ],
      tipsTitle: "Tips from the house",
      tips: [
        "Bring water and cash — cell signal is weak on the beach",
        "Sunscreen and a hat: shade is disputed at noon",
        "On the trail, wear sturdy shoes — there are rocks and roots",
      ],
      coordsLabel: "Coordinates",
      directions: "Get directions on Google Maps",
      mapTitle: "Map of Sete Fontes Beach, Ubatuba",
    },
    trail: {
      title: "The trail map",
      subtitle: "From Saco da Ribeira to our counter, past the seven freshwater springs.",
      start: "Saco da Ribeira",
      startSub: "trailhead",
      springs: "The 7 Springs",
      springsSub: "fresh water on the sand",
      end: "Rainha do Mar",
      endSub: "your kiosk",
      leg1: "~25 min",
      leg2: "~15 min",
      legendWalk: "easy forest walk",
      legendTotal: "total: 30–40 min",
    },
    weather: {
      title: "Right now at Sete Fontes",
      loading: "Checking the sky...",
      unavailable: "Forecast unavailable right now",
      wind: "wind",
      waves: "waves",
      sea: "sea",
      conditions: {
        clear: "Clear sky",
        partly: "Partly cloudy",
        cloudy: "Cloudy",
        fog: "Fog",
        drizzle: "Drizzle",
        rain: "Rain",
        storm: "Thunderstorm",
      },
      seaState: { calm: "calm sea", moderate: "moderate sea", rough: "rough sea" },
    },
    footer: {
      brandSub: "Caiçara Kiosk",
      blurb:
        "True caiçara cooking, craft drinks and the most exclusive sand in Ubatuba. Arrive by trail or boat — leave the rest to us.",
      hoursTitle: "Opening Hours",
      hours: [
        "Tuesday to Sunday · 9am to 6pm",
        "High season and holidays · every day",
        "Monday · crew's day off",
      ],
      reserveCta: "Book a table →",
      whereTitle: "Where We Are",
      addressLine1: "Praia da Sete Fontes, s/n°",
      addressLine2: "Ubatuba — São Paulo's North Coast, Brazil",
      addressLine3: "Access by trail (Praia Grande) or boat (Saco da Ribeira)",
      whatsappCta: "Message on WhatsApp",
      madeWith: "Made with salt, sun and dendê.",
    },
    ambience: {
      soundOn: "Turn off ocean sounds",
      soundOff: "Listen to the ocean",
      phaseLabel: { day: "Beach day", golden: "Golden hour", night: "Caiçara night" },
    },
    whatsappAria: "Chat on WhatsApp",
  },

  es: {
    nav: {
      cardapio: "Menú",
      historia: "Nuestra Historia",
      galeria: "Galería",
      comoChegar: "Cómo Llegar",
      reservar: "Reservar",
      reservarMobile: "Reservar Mesa",
      whatsappAria: "Hablar por WhatsApp",
      openMenu: "Abrir menú",
      closeMenu: "Cerrar menú",
    },
    hero: {
      badge: "Playa Sete Fontes · Ubatuba — SP, Brasil",
      line1: "Pies en la arena,",
      line2: "brisa en el rostro",
      line3: "y el sabor del mar caiçara.",
      subtitle:
        "Un quiosco de paja y madera escondido entre la Mata Atlántica y el mar, donde la pesca del día se vuelve moqueca y la caipirinha llega antes del atardecer.",
      ctaMenu: "Explorar el Menú",
      ctaReserve: "Reservar Mesa",
      stats: ["Pesca fresca del día", "Caipirinhas artesanales", "Acceso por sendero o barco"],
      scrollAria: "Ir al menú",
    },
    manifesto: {
      eyebrow: "Nuestra Historia",
      titleA: "Tres capítulos de una vida",
      titleB: "junto al mar",
      chapters: [
        {
          title: "El refugio secreto de Sete Fontes",
          text: "No se llega en coche — y ese es justamente el encanto. Un sendero suave por la Mata Atlántica, siete fuentes de agua dulce brotando de la arena y, al final del camino, nuestro quiosco de paja esperando con la mesa puesta.",
        },
        {
          title: "Del barco a la olla de barro",
          text: "Trabajamos con los pescadores de la región: lo que el mar da por la mañana, la cocina lo devuelve al almuerzo. Mero, camarón rosa y calamar frescos, preparados al estilo caiçara con dendê, cilantro y lima.",
        },
        {
          title: "Hospitalidad descalza",
          text: "Aquí el código de vestimenta son las chanclas y la sal en la piel. Hamaca para descansar, sombra de un árbol centenario, cerveza enfriando en la hielera y un atardecer que parece no terminar nunca.",
        },
      ],
    },
    menu: {
      eyebrow: "Menú",
      titleA: "Sabores que llegan",
      titleB: "con la marea",
      subtitle:
        "El menú oficial de la casa, directo del mostrador al sitio. Para saber la disponibilidad del día, confirma por WhatsApp.",
      categories: {
        petiscos: "Porciones & à Dorê",
        moquecas: "Moquecas",
        pratos: "Parrilla & Combinados",
        drinks: "Caipirinhas & Tragos",
        bebidas: "Cervezas, Jugos & Bebidas",
        extras: "Ensaladas & Extras",
      },
      groups: {
        porcoes: "Porciones",
        adore: "à Dorê",
        moquecas: "Moquecas",
        grelhados: "Parrilladas",
        combinados: "Combinados",
        vinagrete: "Vinagrete Provençal",
        caipirinhas: "Caipirinhas",
        batidas: "Batidas",
        doses: "Tragos & Dosis",
        cervejas: "Cervezas",
        sucos: "Jugos Naturales",
        bebidasGelo: "Bebidas & Hielo",
        saladas: "Ensaladas",
        extras: "Extras",
        sobremesas: "Postres",
      },
      notes: {
        moquecas: "Sirve 3 · Acompaña arroz y pirão",
        grelhados: "Sirve 3 · Acompaña arroz y farofa de plátano",
        combinados: "Sirve 3",
        vinagrete: "Acompaña pan",
      },
      tags: { house: "Especialidad de la Casa", share: "Para Compartir" },
      featured: {
        badge: "Especialidad de la Casa",
        desc: "Filete de pescado a la parrilla (sororoca) con salsa especial de calamar. Acompañado de arroz y ensalada mixta con palmito y mango, o papas fritas.",
        order: "Pedir por WhatsApp",
      },
    },
    gallery: {
      eyebrow: "Galería",
      titleA: "Un pedazo del paraíso,",
      titleB: "cuadro a cuadro",
      filters: { todos: "Todos", quiosque: "El Quiosco", praia: "La Playa", pratos: "Los Platos" },
      captions: {
        tropical: "El sendero por la Mata Atlántica",
        shrimp: "Banquete caiçara junto al mar",
        coconut: "Caipirinha de limón de la casa",
        sunset: "El atardecer de Sete Fontes",
        caipirinha: "Tragos tropicales junto al mar",
        surf: "Mar abierto para todos los niveles",
        seafood: "Barca de frutos del mar",
        beach: "Agua cristalina todo el año",
        baiaAerea: "Nuestra ensenada vista desde arriba",
        aguaCristalina: "Agua transparente hasta el fondo",
        lanchas: "Lanchas fondeadas en la bahía",
        praiaMata: "Arena dorada abrazada por la mata",
      },
      closeAria: "Cerrar foto",
    },
    reservation: {
      eyebrow: "Reservas",
      titleA: "Asegura tu mesa",
      titleB: "con los pies en la arena",
      subtitle:
        "Los fines de semana y feriados Sete Fontes se llena temprano. Deja tu reserva y guardamos el mejor lugar — de la sombra del árbol al primer banco de la arena.",
      bullets: [
        "Confirmación rápida por WhatsApp",
        "Grupos y cumpleaños son bienvenidos",
        "Martes a domingo, de 9h a 18h",
      ],
      form: {
        name: "Nombre completo *",
        namePh: "¿Cómo te llamamos?",
        phone: "WhatsApp / Teléfono *",
        phonePh: "+55 12 99999-9999",
        date: "Fecha deseada *",
        guests: "Cantidad de personas",
        guestOne: "persona",
        guestMany: "personas",
        guestGroup: "Grupo (9+ personas)",
        seating: "Preferencia de mesa",
        seatingOptions: [
          { id: "Pé na areia", label: "Pies en la arena" },
          { id: "Deck de madeira coberto", label: "Deck de madera techado" },
          { id: "Sombra das árvores", label: "Sombra de los árboles" },
          { id: "Sem preferência", label: "Sin preferencia" },
        ],
        message: "Observaciones",
        messagePh: "Cumpleaños, silla infantil, restricción alimentaria...",
        submit: "Reservar mi mesa",
        sending: "Enviando...",
        note: "Al enviar, podrás confirmar directo por nuestro WhatsApp.",
        errorInvalid: "Completa nombre, teléfono y fecha para reservar.",
        errorFail: "No pudimos registrar ahora. Intenta por el botón de WhatsApp.",
        successToast: "¡Reserva recibida! Confirma por WhatsApp para asegurar tu mesa.",
      },
      success: {
        title: "¡Reserva anotada, {name}!",
        textPre: "Recibimos tu pedido para",
        people: "persona(s)",
        textMid: "el día",
        table: "mesa",
        textPost: "Para asegurarla, confirma con nosotros por WhatsApp:",
        whatsapp: "Confirmar por WhatsApp",
        again: "Hacer otra reserva",
      },
    },
    reach: {
      eyebrow: "Cómo Llegar",
      titleA: "El camino es parte",
      titleB: "de la experiencia",
      subtitle:
        "Sete Fontes no tiene acceso en coche — y por eso sigue salvaje. Elige tu aventura:",
      options: [
        {
          title: "Sendero a pie por la Mata Atlántica",
          time: "30–40 min",
          level: "Suave a moderada",
          steps: [
            "Comienza en Saco da Ribeira, en Ubatuba.",
            "Sigue el sendero marcado por la mata, pasando por las fuentes de agua dulce que dan nombre a la playa.",
            "Al avistar el mar abierto, baja por la arena — el quiosco de paja es el primero a la izquierda.",
          ],
        },
        {
          title: "Barco o escuna taxi",
          time: "10–15 min",
          level: "Sin esfuerzo",
          steps: [
            "Embarca en Saco da Ribeira o en el muelle de Praia Grande.",
            "Acuerda ida y vuelta con el barquero — todos conocen el Quiosque Rainha do Mar.",
            "La travesía pasa por las islas y desembarca directo en nuestra arena.",
          ],
        },
      ],
      tipsTitle: "Consejos de la casa",
      tips: [
        "Lleva agua y dinero en efectivo — la señal de celular es débil en la playa",
        "Protector solar y sombrero: la sombra es disputada al mediodía",
        "En el sendero, usa calzado firme — hay tramos de piedra y raíz",
      ],
      coordsLabel: "Coordenadas",
      directions: "Cómo llegar en Google Maps",
      mapTitle: "Mapa de Playa Sete Fontes, Ubatuba",
    },
    trail: {
      title: "El mapa del sendero",
      subtitle: "Del Saco da Ribeira a nuestro mostrador, pasando por las siete fuentes de agua dulce.",
      start: "Saco da Ribeira",
      startSub: "inicio del sendero",
      springs: "Las 7 Fuentes",
      springsSub: "agua dulce en la arena",
      end: "Rainha do Mar",
      endSub: "tu quiosco",
      leg1: "~25 min",
      leg2: "~15 min",
      legendWalk: "caminata suave por la mata",
      legendTotal: "total: 30–40 min",
    },
    weather: {
      title: "Ahora en Sete Fontes",
      loading: "Consultando el cielo...",
      unavailable: "Pronóstico no disponible ahora",
      wind: "viento",
      waves: "olas",
      sea: "mar",
      conditions: {
        clear: "Cielo despejado",
        partly: "Parcialmente nublado",
        cloudy: "Nublado",
        fog: "Niebla",
        drizzle: "Llovizna",
        rain: "Lluvia",
        storm: "Tormenta",
      },
      seaState: { calm: "mar calma", moderate: "mar moderada", rough: "mar agitada" },
    },
    footer: {
      brandSub: "Quiosco Caiçara",
      blurb:
        "Cocina caiçara de verdad, tragos artesanales y la arena más exclusiva de Ubatuba. Llega por sendero o barco — del resto nos ocupamos nosotros.",
      hoursTitle: "Horario de Atención",
      hours: [
        "Martes a domingo · 9h a 18h",
        "Temporada alta y feriados · todos los días",
        "Lunes · descanso de la tripulación",
      ],
      reserveCta: "Reservar mesa →",
      whereTitle: "Dónde Estamos",
      addressLine1: "Praia da Sete Fontes, s/n°",
      addressLine2: "Ubatuba — Costa Norte de São Paulo, Brasil",
      addressLine3: "Acceso por sendero (Praia Grande) o barco (Saco da Ribeira)",
      whatsappCta: "Escribir por WhatsApp",
      madeWith: "Hecho con sal, sol y dendê.",
    },
    ambience: {
      soundOn: "Apagar el sonido del mar",
      soundOff: "Escuchar el mar",
      phaseLabel: { day: "Día de playa", golden: "Hora dorada", night: "Noche caiçara" },
    },
    whatsappAria: "Chatear por WhatsApp",
  },
};

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState("pt");
  const [db, setDb] = useState(null);

  useEffect(() => {
    document.documentElement.lang = LOCALES[lang];
  }, [lang]);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/content`)
      .then((res) => {
        if (alive) setDb(res.data || {});
      })
      .catch(() => {
        if (alive) setDb({});
      });
    return () => {
      alive = false;
    };
  }, []);

  const dbc = db || {};

  useEffect(() => {
    setWhatsAppNumber((dbc.contact && dbc.contact.whatsapp) || DEFAULT_CONTACT.whatsapp);
  }, [db]); // eslint-disable-line react-hooks/exhaustive-deps

  let t = TRANSLATIONS[lang];
  if (lang === "pt" && dbc.text) t = deepMerge(TRANSLATIONS.pt, dbc.text);

  // Migrate the old highlighted-description text without overriding future custom edits.
  if (lang === "pt" && t.menu?.featured?.desc === "Peixe grelhado, lula ao sugo e camarão servidos na barca de madeira, com arroz, salada e vinagrete. Serve 3 a 4 pessoas.") {
    t = { ...t, menu: { ...t.menu, featured: { ...t.menu.featured, desc: "Filé de peixe grelhado (sororoca) com molho especial de lula. Acompanha arroz e salada mista com palmito e manga ou batatas fritas." } } };
  }

  const content = {
    menu: removeImperioFromMenu(normalizeLegacyBarcaDoReiMenu(dbc.menu || DEFAULT_MENU)),
    featured: normalizeFeatured({ ...DEFAULT_FEATURED, ...(dbc.featured || {}) }),
    contact: { ...DEFAULT_CONTACT, ...(dbc.contact || {}) },
    images: { ...DEFAULT_IMAGES, ...(dbc.images || {}) },
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, content }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
